import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { adminApi, getAdminMe } from '../lib/adminApi';
import { Users, Plus, Pencil, Trash2, KeyRound, ShieldCheck, ShieldOff, Copy, X, Save } from 'lucide-react';

const ROLE_BADGES = {
  super_admin: { label: 'Super Admin', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  admin: { label: 'Admin', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  manager: { label: 'Manager', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  account: { label: 'Account', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  staff: { label: 'Staff', cls: 'bg-gray-100 text-gray-800 border-gray-200' },
};

const ALL_MODULES = ['vendors', 'products', 'customers', 'orders', 'leads', 'settings', 'cms', 'categories', 'cities', 'services', 'users', 'audit', 'exports', 'payments'];
const ALL_ACTIONS = ['read', 'write', 'delete'];

const emptyForm = {
  email: '', password: '', name: '', mobile: '',
  role: 'staff', mfa_enabled: false, must_change_password: true,
  permissions: { grants: [], revokes: [] },
};

const AdminUsersPanel = () => {
  const { toast } = useToast();
  const me = getAdminMe();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tempPwd, setTempPwd] = useState(''); // temp pwd displayed after reset

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.listAdminUsers();
      setItems(data.items || []);
      setRoles(data.roles || []);
      setRoleDefaults(data.role_defaults || {});
    } catch (err) {
      toast({ title: 'Failed to load users', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, password: '', name: u.name || '', mobile: u.mobile || '',
      role: u.role, mfa_enabled: !!u.mfa_enabled,
      must_change_password: !!u.must_change_password,
      permissions: u.permissions && (u.permissions.grants || u.permissions.revokes)
        ? { grants: u.permissions.grants || [], revokes: u.permissions.revokes || [] }
        : { grants: [], revokes: [] },
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.email.trim() || !form.name.trim() || !form.role) {
      return toast({ title: 'Email, name and role are required', variant: 'destructive' });
    }
    if (!editing && (!form.password || form.password.length < 8)) {
      return toast({ title: 'Password must be at least 8 characters', variant: 'destructive' });
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, mobile: form.mobile, role: form.role,
          mfa_enabled: form.mfa_enabled, permissions: form.permissions,
        };
        await adminApi.updateAdminUser(editing.id, payload);
        toast({ title: 'User updated' });
      } else {
        await adminApi.createAdminUser(form);
        toast({ title: 'User created' });
      }
      closeForm();
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await adminApi.updateAdminUser(u.id, { is_active: !u.is_active });
      toast({ title: u.is_active ? 'User disabled' : 'User enabled' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const resetPwd = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? They will have to set a new one on next login.`)) return;
    try {
      const r = await adminApi.resetAdminUserPassword(u.id);
      setTempPwd(`${u.email}: ${r.temporary_password}`);
      toast({ title: 'Temporary password generated', description: 'Copy and share with the user.' });
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) return toast({ title: 'You cannot delete your own account', variant: 'destructive' });
    if (!window.confirm(`Permanently delete admin user ${u.email}?`)) return;
    try {
      await adminApi.deleteAdminUser(u.id);
      toast({ title: 'User deleted' });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const toggleOverride = (kind, key) => {
    const list = new Set(form.permissions[kind] || []);
    if (list.has(key)) list.delete(key);
    else list.add(key);
    setForm({ ...form, permissions: { ...form.permissions, [kind]: Array.from(list) } });
  };

  const renderEffectivePerm = (module, action) => {
    const base = roleDefaults[form.role] || {};
    const allowed = (base[module] || base['*'] || []);
    const hasBase = allowed.includes(action) || allowed.includes('*');
    const key = `${module}:${action}`;
    if ((form.permissions.revokes || []).includes(key) || (form.permissions.revokes || []).includes(`${module}:*`)) return 'denied';
    if ((form.permissions.grants || []).includes(key) || (form.permissions.grants || []).includes(`${module}:*`)) return 'granted';
    return hasBase ? 'inherited' : 'denied';
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2c54b2]" /> Admin User Management
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Manage backstage accounts, roles, and per-user permissions. Super Admin role is required to mutate Super Admin accounts.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="admin-user-add-btn">
          <Plus className="w-4 h-4 mr-1" /> New User
        </Button>
      </div>

      {tempPwd && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-md p-3 text-sm flex items-center justify-between gap-3" data-testid="temp-pwd-banner">
          <div>
            <strong>Temporary password generated:</strong> <code className="font-mono">{tempPwd}</code>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(tempPwd); toast({ title: 'Copied' }); }}><Copy className="w-3 h-3 mr-1" />Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setTempPwd('')}><X className="w-3 h-3" /></Button>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No admin users yet. Click <strong>New User</strong> to add one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">MFA</th>
                  <th className="px-3 py-2">Active</th>
                  <th className="px-3 py-2">Last login</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const badge = ROLE_BADGES[u.role] || ROLE_BADGES.staff;
                  return (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-3 py-2 font-medium">{u.email}{u.id === me?.id ? <span className="ml-2 text-[10px] text-gray-500">(you)</span> : null}</td>
                      <td className="px-3 py-2">{u.name || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="px-3 py-2">
                        {u.mfa_enabled
                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3" />On</span>
                          : <span className="inline-flex items-center gap-1 text-gray-500"><ShieldOff className="w-3 h-3" />Off</span>}
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => toggleActive(u)} disabled={u.id === me?.id} className={`text-xs font-semibold px-2 py-0.5 rounded ${u.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'} ${u.id === me?.id ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`} data-testid={`toggle-active-${u.id}`}>
                          {u.is_active ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resetPwd(u)} title="Reset password" data-testid={`reset-pwd-${u.id}`}>
                          <KeyRound className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(u)} className="text-red-600 border-red-200 hover:bg-red-50" disabled={u.id === me?.id} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-user-form">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-base font-semibold">{editing ? `Edit ${editing.email}` : 'Create admin user'}</h3>
              <Button size="sm" variant="ghost" onClick={closeForm}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} placeholder="user@telanganamart.com" data-testid="form-email" />
                </div>
                <div>
                  <Label className="text-xs">Full name <span className="text-red-500">*</span></Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="form-name" />
                </div>
                {!editing && (
                  <div>
                    <Label className="text-xs">Initial password <span className="text-red-500">*</span></Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" data-testid="form-password" />
                  </div>
                )}
                <div>
                  <Label className="text-xs">Mobile (for MFA)</Label>
                  <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit mobile" data-testid="form-mobile" />
                </div>
                <div>
                  <Label className="text-xs">Role <span className="text-red-500">*</span></Label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm bg-white"
                    data-testid="form-role"
                  >
                    {roles.map((r) => <option key={r} value={r}>{ROLE_BADGES[r]?.label || r}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-4 pt-5">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.mfa_enabled} onChange={(e) => setForm({ ...form, mfa_enabled: e.target.checked })} data-testid="form-mfa" /> Enable MFA (OTP)
                  </label>
                  {!editing && (
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.must_change_password} onChange={(e) => setForm({ ...form, must_change_password: e.target.checked })} data-testid="form-must-change" /> Force password change
                    </label>
                  )}
                </div>
              </div>

              {/* Permission matrix */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Permissions for <strong>{ROLE_BADGES[form.role]?.label || form.role}</strong></h4>
                <p className="text-xs text-gray-500 mb-3">Inherited from the role by default. Click a cell to <strong>Grant</strong> or <strong>Revoke</strong> as a per-user override.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-2 py-1.5 border border-gray-200">Module</th>
                        {ALL_ACTIONS.map((a) => <th key={a} className="px-2 py-1.5 border border-gray-200 capitalize text-center">{a}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {ALL_MODULES.map((m) => (
                        <tr key={m}>
                          <td className="px-2 py-1.5 border border-gray-200 font-mono text-[11px]">{m}</td>
                          {ALL_ACTIONS.map((a) => {
                            const status = renderEffectivePerm(m, a);
                            const key = `${m}:${a}`;
                            const colorMap = {
                              inherited: 'bg-blue-50 text-blue-800 border-blue-100',
                              granted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                              denied: 'bg-gray-100 text-gray-500 border-gray-200',
                            };
                            return (
                              <td key={a} className={`border border-gray-200 text-center cursor-pointer hover:opacity-80 ${colorMap[status]}`} onClick={() => {
                                // toggle: if currently inherited → revoke; if inherited(denied) → grant; if granted → clear; if revoked → clear
                                const grants = new Set(form.permissions.grants || []);
                                const revokes = new Set(form.permissions.revokes || []);
                                if (status === 'inherited') { revokes.add(key); grants.delete(key); }
                                else if (status === 'granted') { grants.delete(key); }
                                else if (status === 'denied') {
                                  if (revokes.has(key) || revokes.has(`${m}:*`)) { revokes.delete(key); }
                                  else { grants.add(key); }
                                }
                                setForm({ ...form, permissions: { grants: Array.from(grants), revokes: Array.from(revokes) } });
                              }} data-testid={`perm-${m}-${a}`}>
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase">
                                  {status === 'inherited' ? '✓' : status === 'granted' ? '+✓' : '×'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-[11px] text-gray-500 mt-2 flex items-center gap-4">
                  <span><span className="inline-block w-3 h-3 bg-blue-50 border border-blue-100 align-middle mr-1" /> inherited</span>
                  <span><span className="inline-block w-3 h-3 bg-emerald-100 border border-emerald-200 align-middle mr-1" /> granted (override)</span>
                  <span><span className="inline-block w-3 h-3 bg-gray-100 border border-gray-200 align-middle mr-1" /> denied / revoked</span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <Button variant="outline" onClick={closeForm}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="form-save-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create user')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPanel;
